import * as React from "react";
import {useCallback, useEffect, useState} from "react";

import {ACard, ADataGrid, ADataGridFilter, PagedSearchParams} from "@atiautomacao/ati-ui-library";

import {AppBar, Box, Grid, Stack, TextField, Toolbar, Typography, useTheme} from "@mui/material";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {QueryClient, QueryClientProvider} from "react-query";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LoadingButton } from "@mui/lab";
import { Download } from "@mui/icons-material";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";

import {differenceInDays, isValid} from "date-fns";
import { useSnackbar } from "notistack";
import dayjs, { Dayjs } from "dayjs";

import {DateFormat, LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";
import {postAndDownloadFile} from "../../Reports/ManagementReport/utils/postAndDownloadFile";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {hideDashboardHeaderMenu} from "../../Dashboard/DashboardMenuReducer";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {AuditAlarmsQuery} from "./AuditAlarmsReactQuery";
import {useDebounce} from "../../../Utils/UseDebounce";
import {ActionConfig} from "../../Dashboard/Alarms/AlarmPage";
import {initColumnsData} from "./Columns";

import { AUTHORITIES, GATEWAY_URL } from "../../../Config/Constants";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import {
    applyFiltersToColumns,
    buildDateFilterSanitizationMessage,
    buildDateFilterValidationMessage,
    mergePendingDateFilterInputValues,
    sanitizeDateFilters,
    validateAndConvertDateFilters,
} from "../../../Utils/AlarmDateFilterValidationUtils";

export interface AlarmRow {
    id: string;
    selected?: boolean;
    teleObjectId: number;
    severityId: number;
    severityName: string;
    severityColor: string;
    description: string;
    macroRegionName: string;
    regionName: string;
    powerStationId: number;
    powerStationName: string | JSX.Element;
    skidId: number;
    skidName: string | JSX.Element;
    equipmentId: number;
    equipmentName: string;
    clearDate?: Date | string;
    acknowledgementDate?: Date | string;
    dateTime: Date | string;
    dateTimeNote: Date | string;
    acknowledgedUser: string;
    clearedUser: string;
    acknowledgementNote: string;
    descriptionNote: string;
    userNameNote: string;
    group: boolean | any;
    actionsConfig?: Array<ActionConfig>

}

const MAX_RANGE_DAYS = 60;

function AuditAlarms(){
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const isAuthenticated = useAppSelector((state: any) => state.authentication.isAuthenticated);
    const openMainMenu = useAppSelector((state: any) => state.layout.openMainMenu);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const contentPagePaddingTop = useAppSelector((state: any) => state.layout.contentPagePaddingTop);

    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_AUDIT, AUTHORITIES.VIEW_AUDIT, AUTHORITIES.VIEW_AUDIT_ALARMS,
    ]);
    const isAuthorizedToSearchByExcel = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_AUDIT, AUTHORITIES.VIEW_AUDIT, AUTHORITIES.VIEW_AUDIT_ALARMS,
    ]);

    const dispatch = useAppDispatch();

    const handleStartDateTime = (newValue: Date) => {
        const firstDayOfMonth = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);
        return firstDayOfMonth;
    };
    const [startDateTime, setStartDateTime] = React.useState<Date>(handleStartDateTime(new Date()));
    const [endDateTime, setEndDateTime] = React.useState<Date>(new Date());
    const [startDateInput, setStartDateInput] = useState<Dayjs | null>(dayjs(startDateTime));
    const [endDateInput, setEndDateInput] = useState<Dayjs | null>(dayjs(endDateTime));
    const [dateRangeError, setDateRangeError] = useState<string | null>(null);
    
    const { debouncedFn } = useDebounce(3000);
    const [debouncedDateRange, setDebouncedDateRange] = useState(() => ({
        start: startDateTime,
        end: endDateTime
    }));

    const [rows, setRows] = React.useState<AlarmRow[]>([]);
    const [totalOfRecords, setTotalOfRecords] = useState(0);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumnsData), 0, 50));
    const [columns, setColumns] = useState(() => initColumnsData);

    useEffect(() => {
        if (dateRangeError) return;
        debouncedFn(() => {
            setDebouncedDateRange({
                start: startDateTime,
                end: endDateTime
            });
        });
    }, [startDateTime, endDateTime, dateRangeError, debouncedFn]);

    const { data, isLoading, isSuccess } = AuditAlarmsQuery(
        pagedSearchParams,
        debouncedDateRange.start,
        debouncedDateRange.end
    );

    const isExportEmpty = isSuccess && (data?.totalOfRecords ?? 0) === 0;
    const isExportDisabled = Boolean(dateRangeError) || isExportEmpty;

 

    const [isDownloadCsv, setIsDownloadCsv] = useState(false);
    const [isDownloadExcel, setIsDownloadExcel] = useState(false);

    useEffect(() => {
        dispatch(hideDashboardHeaderMenu())
    }, [dispatch]);

    const updateRows = (data: any) => {
        const filteredData = data?.data?.map((alarm: any) => {
            return {
                teleObjectId: alarm.teleObjectId,
                selected:false,
                id: alarm.id.toString(),
                group: alarm.group,
                severityId: alarm.severityId,
                severityName: alarm.severityName,
                severityColor: alarm.severityColor,
                description: alarm.description,
                macroRegionName: alarm.macroRegionName,
                regionName: alarm.regionName,
                powerStationId: alarm.powerStationId,
                powerStationName: alarm.powerStationName,
                skidId: alarm.skidId,
                skidName: alarm.skidName,
                equipmentId: alarm.equipmentId,
                equipmentName: alarm.equipmentName,
                clearDate: alarm.clearDate ? DateFormat(alarm.clearDate) : "",
                acknowledgementDate: alarm.acknowledgementDate ? DateFormat(alarm.acknowledgementDate) : "",
                dateTime: DateFormat(alarm.dateTime),
                clearedUser: alarm.clearedUser,
                acknowledgedUser: alarm.acknowledgedUser,
                descriptionNote: alarm.descriptionNote,
                dateTimeNote: alarm.dateTimeNote ? DateFormat(alarm.dateTimeNote) : undefined,
                userNameNote: alarm.userNameNote,
                actionsConfig: [
                    {actionName: "grouping", disabled: false},
                    {actionName: "note", disabled: false},
                ],
            };
        }).sort((a:any, b:any) => {
            return new Date(b.dateTimeReq).getTime() - new Date(a.dateTimeReq).getTime();
        });

        setRows(filteredData ?? []);
    };

    useEffect(() => {
        if (isSuccess && data?.totalOfRecords !== undefined) {
            setTotalOfRecords(data?.totalOfRecords)
        }
    }, [data?.data]);

    useEffect(() => {
        updateRows(data);
    }, [data?.data]);

    const showDateFilterSanitizationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterSanitizationMessage(fieldLabels), {variant: 'warning'});
    }, [enqueueSnackbar]);

    const showDateFilterValidationMessage = useCallback((fieldLabels: Array<string>) => {
        enqueueSnackbar(buildDateFilterValidationMessage(fieldLabels), {variant: 'error'});
    }, [enqueueSnackbar]);

    const validatePagedSearchParams = useCallback((params: PagedSearchParams): PagedSearchParams | null => {
        const validationResult = validateAndConvertDateFilters(params.filters);

        if (!validationResult.isValid) {
            showDateFilterValidationMessage(validationResult.invalidFieldLabels);
            return null;
        }

        return new PagedSearchParams(validationResult.filters, params.page, params.size);
    }, [showDateFilterValidationMessage]);


    const getValidatedExportParams = (): PagedSearchParams | null => {
        if (dateRangeError || !isValid(startDateTime) || !isValid(endDateTime)) {
            enqueueSnackbar("Informe datas válidas no período.", {variant: 'error'});
            return null;
        }

        const currentFilters = mergePendingDateFilterInputValues(
            "alarm-audit-grid",
            extractFiltersFromColumns(columns),
            columns
        );
        const sanitizedResult = sanitizeDateFilters(currentFilters);

        setColumns(applyFiltersToColumns(initColumnsData, sanitizedResult.filters));

        if (sanitizedResult.sanitizedFieldLabels.length > 0) {
            showDateFilterSanitizationMessage(sanitizedResult.sanitizedFieldLabels);
            return null;
        }

        const newPageParams = new PagedSearchParams(
            JSON.parse(JSON.stringify(sanitizedResult.filters)),
            pagedSearchParams.page,
            pagedSearchParams.size
        );
        newPageParams.filters.push({name: "dateTime", condition: "starts-at", value: LocalDateTimeFormatISO(startDateTime), sort: "none"});
        newPageParams.filters.push({name: "dateTime", condition: "ends-at", value: LocalDateTimeFormatISO(endDateTime), sort: "none"});

        return validatePagedSearchParams(newPageParams);
    };

    const getOnClickByType = (type: string) => {
        const validatedParams = getValidatedExportParams();
        if (!validatedParams) return;

        if (type === "excel") {
            setIsDownloadExcel(true);
            return handleDownloadExcel(validatedParams)
        }
        setIsDownloadCsv(true);
        return handleDownloadCSV(validatedParams)
        
    }

    const handleDownloadExcel = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/alarm/excel?`
        postAndDownloadFile(apiUrl, newPageParams.toURLSearchParams(), 'auditoria_de_alarmes.xlsx',   
        () => {
            enqueueSnackbar("Sucesso ao baixar o Excel", {variant: 'success'});
            setIsDownloadExcel(false);
        },
        (error) => {
            enqueueSnackbar("Erro ao baixar o  Excel " + error, {variant: 'error'});
            setIsDownloadExcel(false);
        },
        () => {
            enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
            setIsDownloadExcel(false);
        });
    };

    const handleDownloadCSV = (newPageParams: PagedSearchParams) => {
        const apiUrl = GATEWAY_URL+`api/audit/alarm/csv?`
        postAndDownloadFile(apiUrl, newPageParams.toURLSearchParams(), 'auditoria_de_alarmes.csv',
        () => {
            enqueueSnackbar("Sucesso ao baixar o CSV", {variant: 'success'});
            setIsDownloadCsv(false);
        },
        (error) => {
            enqueueSnackbar("Erro ao baixar o  CSV " + error, {variant: 'error'});
            setIsDownloadCsv(false);
        },
        () => {
            enqueueSnackbar("Dados não encontrados para esse período", {variant: 'warning'});
            setIsDownloadCsv(false);
        });
        
    };

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        const sanitizedResult = sanitizeDateFilters(dataGridFilters);

        setColumns(applyFiltersToColumns(initColumnsData, sanitizedResult.filters));

        if (sanitizedResult.sanitizedFieldLabels.length > 0) {
            showDateFilterSanitizationMessage(sanitizedResult.sanitizedFieldLabels);
            return;
        }

        const newPageParams = new PagedSearchParams(sanitizedResult.filters, page, pageSize);
        const validatedParams = validatePagedSearchParams(newPageParams);

        if (!validatedParams) return;

        setPagedSearchParams(
            validatedParams
        );
    }, [showDateFilterSanitizationMessage, validatePagedSearchParams]);

    function validateDateRange(
        start: Date | null,
        end: Date | null,
        changedField: 'start' | 'end',
        maxRangeDays: number = MAX_RANGE_DAYS,
    ): string | null {
        if (!start || !end || !isValid(start) || !isValid(end)) {
            return null;
        }

        const diffDays = differenceInDays(end, start);
        if (diffDays < 0) {
            return changedField === 'start'
                ? 'A data inicial não pode ser maior que a data final.'
                : 'A data final não pode ser menor que a data inicial.';
        }

        if (diffDays > maxRangeDays) {
            return `O período máximo permitido é de ${maxRangeDays} dias.`;
        }

        return null;
    }

    const handleRangeStartChange = (newValue: Dayjs | null) => {

        setStartDateInput(newValue);

        if (!newValue || !newValue.isValid()) {
            setDateRangeError("Data inicial inválida.");
            enqueueSnackbar("Informe uma data válida no campo Início.", { variant: 'error' });
            return;
        }

        const newDate = newValue.toDate();
        const error = validateDateRange(newDate, endDateTime, 'start');
        setStartDateTime(newDate);
        if (error) {
            setDateRangeError(error);
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }

        setDateRangeError(null);
    };

    const handleRangeEndChange = (newValue: Dayjs | null) => {
        setEndDateInput(newValue);

        if (!newValue || !newValue.isValid()) {
            setDateRangeError("Data final inválida.");
            enqueueSnackbar("Informe uma data válida no campo Fim.", { variant: 'error' });
            return;
        }

        const newDate = newValue.toDate();
        const error = validateDateRange(startDateTime, newDate, 'end');
        setEndDateTime(newDate);
        if (error) {
            setDateRangeError(error);
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }

        setDateRangeError(null);
    };

    const isSmallScreen = window.innerWidth <= 400;
    const isMedianScreen = window.innerWidth <= 495;
    return (
        <>
            <AppBar style={{ paddingTop: isSmallScreen ? 95 : isMedianScreen ? 84 : contentPagePaddingTop  }}>
                <Toolbar
                    sx={{
                        paddingTop: 2,
                        marginLeft: {
                            xs: "initial", md: isAuthenticated ? (
                                openSubMenu ? (
                                    openMainMenu ? `${400}px` : `${80}px`
                                ) : (
                                    openMainMenu ? `${80}px` : `${0}` // Set margin to 50 if submenu closed and main menu open
                                )
                            ) : 'initial'
                        },
                        backgroundColor: theme.palette.background.default
                    }}
                >
                    <Grid container component="fieldset"  sx={{ borderColor: 'transparent', padding: '0 0 4px 16px', mb: '5px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label="Início"
                                ampm={false}
                                inputFormat="DD/MM/YYYY HH:mm"
                                value={startDateInput}
                                onChange={handleRangeStartChange}
                                renderInput={(params: any) => <TextField
                                    variant={"standard"}
                                    error={!!dateRangeError}
                                    sx={{ width: '200px', mr: 2 }} {...params}     
                                     FormHelperTextProps={{
                                        sx: { color:"#d32f2f"}
                                    }}                              
                                    helperText={dateRangeError ? dateRangeError : " "}
                                />}
                            />
                            <DateTimePicker
                                label="Fim"
                                ampm={false}
                                inputFormat="DD/MM/YYYY HH:mm"
                                value={endDateInput}
                                onChange={handleRangeEndChange}
                                renderInput={(params: any) => <TextField variant={"standard"} error={!!dateRangeError} sx={{ width: '200px' }} {...params} />}
                            />
                            
                        </LocalizationProvider>
                        
                        <Grid item sm={4}>
                            {
                                isDownloadCsv ?
                                    <LoadingButton
                                        loading
                                        loadingPosition="end"
                                        endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                                    >
                                        Carregando..
                                    </LoadingButton>
                                    :
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={!isAuthorizedToSearchByCsv || isExportDisabled}
                                        onClick={() => getOnClickByType("csv")}
                                        startIcon={<Download />}
                                        sx={{ mr: 1, ml: 1, mt: 1 }}
                                    >
                                        CSV
                                    </Button>
                            }
                            {
                                isDownloadExcel ?
                                    <LoadingButton
                                        loading
                                        loadingPosition="end"
                                        endIcon={<SaveIcon sx={{ fontSize: 20 }} />}
                                    >
                                        Carregando..
                                    </LoadingButton>
                                    :
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={!isAuthorizedToSearchByExcel || isExportDisabled}
                                        onClick={() => getOnClickByType("excel")}
                                        startIcon={<Download />}
                                        sx={{ mr: 1,mt: 1 }}
                                    >
                                        Excel
                                    </Button>
                            }
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>

            <Grid item sx={{marginTop:isMedianScreen ? 12 :8, pl: isMedianScreen ? 0: 1}}>
                <ACard title="Auditoria de alarmes"
                    styleProps={{
                        titleStyle: {
                            fontWeight: 400,
                            fontSize: '1.5rem',
                            lineHeight: 1.334,
                        }
                    }}
                    >
                    <ADataGrid
                        ariaLabel={'alarm-audit-grid'}
                        hideFilters={false}
                        showFilterOptions={false}
                        hideSelection={true}
                        size={"small"}
                        headerStyle={{
                            paddingTop: 2
                        }}
                        columns={columns}
                        rows={rows}
                        page={pagedSearchParams.page}
                        loading={isLoading}
                        totalOfRecords={totalOfRecords}
                        onFiltersChange={handleOnFiltersChange}
                        rowsPerPage={pagedSearchParams.size}
                        actionColumnWidth={50}
                        maxHeight={700}
                    />
                    {rows.length === 0 && !isLoading &&
                    // TODO: Substituir Box customizado por componente EmptyState quando componente estiver disponível
                    // Rastreador: SGD-3420
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                minHeight: '200px',
                                padding: 4,
                                backgroundColor: '#f8faff',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: '#f0f4f8',
                                height: '377px',
                            }}
                        >
                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box
                                    sx={{
                                        fontSize: 40,
                                        display: 'flex',
                                        color: '#3b82f6',
                                        mb: 0.5,
                                    }}
                                >
                                    <SearchOutlinedIcon />
                                </Box>

                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1a202c',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Alarmes não encontrados
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#718096',
                                        maxWidth: '300px',
                                    }}
                                >
                                    Ajustes os critérios de busca utilizados e tente novamente.
                                </Typography>
                            </Stack>
                        </Box>
                    }
                </ACard>
            </Grid>
        </>
    )
}

export default function AuditAlarmsPage() {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <AuditAlarms/>
        </QueryClientProvider>
    )
}
